import { parseSnbt } from "./snbtReader";
import { isOk, type useApi } from "../api";
import type { WizardItemSource } from "../components/ItemIdentityWizard";
import type { ItemStack } from "../models/itemStack";
import type { AhListingDto, ItemStackDto } from "../api/generated/schemas";

export type ListingStatus = "active" | "ending" | "expired";

const ENDING_WINDOW_MS = 120_000;

export function statusOf(listing: AhListingDto, nowMs: number): ListingStatus {
  if (listing.expiredAt) return "expired";
  const expiry = listing.estimatedExpiryUtc ? Date.parse(listing.estimatedExpiryUtc) : 0;
  if (expiry && expiry - nowMs <= ENDING_WINDOW_MS) return "ending";
  return "active";
}

export function remainingMs(listing: AhListingDto, nowMs: number): number {
  if (!listing.estimatedExpiryUtc) return 0;
  return Date.parse(listing.estimatedExpiryUtc) - nowMs;
}

export function formatRemaining(ms: number): string {
  if (ms <= 0) return "0s";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function toStack(listing: AhListingDto): ItemStack {
  return {
    itemId: listing.itemId ?? 0,
    count: 1,
    damage: listing.itemDamage ?? 0,
    name: listing.itemName,
    snbt: listing.snbt ?? undefined,
  };
}

function toStackDto(listing: Pick<AhListingDto, "id" | "itemId" | "itemDamage" | "snbt">): ItemStackDto {
  return {
    key: listing.id, itemId: listing.itemId ?? 0, metadata: listing.itemDamage ?? 0,
    count: 1, snbt: listing.snbt ?? null, nbtHash: null, isCustom: false,
  };
}

// The identity wizard's item pool for AH watches: current and past listings alike (status left unset),
// so a watch can be built from an item that already sold as easily as one still up for auction.
export function ahListingWizardSource(api: ReturnType<typeof useApi>, placeholderKey: string): WizardItemSource {
  return {
    placeholderKey,
    search: async (query) => {
      const res = await api.ah.getListings({ search: query, pageSize: 30 });
      return isOk(res.data) ? res.data.value!.items.map(toStackDto) : [];
    },
  };
}

function stripColors(text: string): string {
  return text.replace(/§./g, "").trim();
}

// GrieferGames injects auction chrome (the ░░░ separator, bid, remaining time, "click to open")
// into each listing's lore. Keep only the item's own lore above the separator.
export function listingLore(snbt?: string | null): string[] {
  const lore = parseSnbt(snbt ?? undefined).lore ?? [];
  const separator = lore.findIndex(line => line.includes("░"));
  const own = separator >= 0 ? lore.slice(0, separator) : lore;

  let start = 0;
  let end = own.length;
  while (start < end && stripColors(own[start]) === "") start++;
  while (end > start && stripColors(own[end - 1]) === "") end--;
  return own.slice(start, end);
}
